import { lockSalesTaxFiling, updateTask } from '@commenda/api';
import { Button, Icons, Tooltip, useToggle } from '@commenda/kakashi';
import { TaskParty, TaskStatus } from '@prisma/client';
import { useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { FilingReviewStatus } from '../../../molecules';
import { useSalesTaxFilingDetails } from '../../../sales-tax/filing/dashboard/useSalesTaxFilingDetail';
import { ViewTransactionsModal } from '../../../sales-tax/transactions-tab/TransactionView/ViewTransactionsModal';
import { ConfirmationModal } from '../../../molecules/modals/ConfirmationModal';
import { ReportIssueModal } from '../../common/modals/ReportIssueModal';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { FilingApprovalModal } from './components/FilingApprovalModal';

export const ApproveFilingTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate: refetchServiceRequest } = context;

  const { setNotification } = useNotification();

  const approvalModalVisibility = useToggle();
  const viewTransactionsModalVisibility = useToggle();
  const reReviewModalVisibility = useToggle();
  const markAsCompletedModalVisibility = useToggle();

  const isWorkflowTask = !!task.workflowDefinitionStepId;

  const completedLabel =
    task.metadata?.isIssueReported === true
      ? 'Sent back for re-review'
      : 'Filing approved';

  const onRequestReReview = async () => {
    await updateTask({
      id: task.id,
      data: { status: TaskStatus.COMPLETED, isIssueReported: true },
    });
    reReviewModalVisibility.off();
    if (refetchServiceRequest) refetchServiceRequest();
  };

  const onMarkAsCompleted = async () => {
    const res = await updateTask({
      id: task.id,
      data: { status: TaskStatus.COMPLETED },
    });
    if (!res.ok) {
      setNotification({
        title: 'Error',
        message: 'Failed to mark as completed',
        show: true,
        valid: false,
      });
      throw new Error('Failed to mark as completed');
    }
    if (refetchServiceRequest) refetchServiceRequest();
  };

  const { filing, refetchFiling } = useSalesTaxFilingDetails({
    filingId: serviceRequest?.salesTaxFilingId,
    corporationId: serviceRequest?.corporationId,
  });

  const viewTransactionsButton = (
    <Button
      size="sm"
      variant="tertiary"
      onClick={viewTransactionsModalVisibility.show}
    >
      View transactions <Icons.ArrowUpRight className="size-4" />
    </Button>
  );

  const clientActionableActions = (
    <div className="flex items-center gap-xl">
      {isWorkflowTask && (
        <Tooltip
          side="right"
          content={
            filing?.filingStatus === FilingReviewStatus.FILED
              ? 'Filing is already filed. You cannot request re-review'
              : 'Request the filing to be re-reviewed'
          }
        >
          <Button
            size="sm"
            variant="tertiary"
            onClick={reReviewModalVisibility.show}
            disabled={filing?.filingStatus === FilingReviewStatus.FILED}
          >
            Request re-review
          </Button>
        </Tooltip>
      )}

      <Tooltip
        side="right"
        content={
          filing?.filingStatus === FilingReviewStatus.FILED
            ? 'Filing is already filed. You cannot approve this filing'
            : 'Approve this filing'
        }
      >
        <Button
          variant="secondary"
          size="sm"
          onClick={approvalModalVisibility.show}
          disabled={filing?.filingStatus === FilingReviewStatus.FILED}
        >
          Approve
        </Button>
      </Tooltip>
    </div>
  );

  const agentPendingState = {
    actions: (
      <div className="flex items-center gap-4">
        {viewTransactionsButton}
        <Button
          size="sm"
          variant="secondary"
          onClick={markAsCompletedModalVisibility.show}
        >
          <Icons.Check className="w-4" />
          Mark as completed on client's behalf
        </Button>
      </div>
    ),
    label: <ServiceRequestTaskLabel label="Client approval pending" />,
  };

  const operatorStateMap = {
    [TaskStatus.TO_DO]: agentPendingState,
    [TaskStatus.IN_PROGRESS]: agentPendingState,
    [TaskStatus.BACKLOG]: {
      actions: viewTransactionsButton,
      label: (
        <ServiceRequestTaskLabel label="Enable filing for customer to approve" />
      ),
    },
    [TaskStatus.COMPLETED]: {
      actions: viewTransactionsButton,
      label: (
        <ServiceRequestTaskLabel label={completedLabel} status={task.status} />
      ),
    },
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: clientActionableActions,
            label: (
              <ServiceRequestTaskLabel label="Review transactions and approve filing" />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: clientActionableActions,
            label: (
              <ServiceRequestTaskLabel
                label="Review transactions and approve filing"
                status={task.status}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: viewTransactionsButton,
            label: (
              <ServiceRequestTaskLabel
                label={completedLabel}
                status={task.status}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Review transactions and approve filing" />
            ),
          },
        },

        [TaskParty.AGENT]: operatorStateMap,
        [TaskParty.ADMIN]: operatorStateMap,
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  const [isApprovingFiling, setIsApprovingFiling] = useState(false);

  const onApproveFilingTask = async () => {
    if (!filing) {
      return;
    }

    setIsApprovingFiling(true);
    const response = await lockSalesTaxFiling(
      filing.id,
      serviceRequest.corporationId
    );

    if (response.success) {
      if (refetchFiling) refetchFiling();
      if (refetchServiceRequest) refetchServiceRequest();

      approvalModalVisibility.off();
    } else {
      setNotification({
        title: 'Error approving filing',
        message: response.error,
        show: true,
        valid: false,
      });
      console.error(response.error);
    }

    setIsApprovingFiling(false);
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      {filing && (
        <FilingApprovalModal
          isOpen={approvalModalVisibility.visible}
          onClose={approvalModalVisibility.off}
          onApprove={onApproveFilingTask}
          isLoading={isApprovingFiling}
        />
      )}

      {filing && (
        <ViewTransactionsModal
          isOpen={viewTransactionsModalVisibility.visible}
          onClose={viewTransactionsModalVisibility.off}
          corporationId={serviceRequest.corporationId}
          filing={filing}
          companyId={serviceRequest.companyId}
        />
      )}

      {serviceRequest && (
        <ReportIssueModal
          title="Request re-review"
          description="Describe what should be re-reviewed. This message will be sent to Threads."
          isOpen={reReviewModalVisibility.visible}
          setIsOpen={reReviewModalVisibility.setValue}
          onReport={onRequestReReview}
          serviceRequest={serviceRequest}
        />
      )}

      <ConfirmationModal
        isOpen={markAsCompletedModalVisibility.visible}
        onClose={markAsCompletedModalVisibility.hide}
        onConfirm={onMarkAsCompleted}
        title="Mark as completed"
        message="The client approval step will be marked as completed. To revert this change, please contact Commenda support."
        confirmButtonText="Continue"
        hideCancelButton
      />
    </div>
  );
};
